import { apiClient } from "@/lib/api";
import { fetchAllPages } from "@/lib/fetchAllPages";
import type {
  Device,
  DeviceCreate,
  DeviceCreateResponse,
  DeviceUpdate,
  PaymentReader,
} from "@/types";

/** Backend cap: GET /devices `limit` le=500. */
const DEVICES_PAGE_SIZE = 500;

type DeviceListFilters = {
  house_id?: string;
  region_id?: string;
};

export const deviceKeys = {
  all: ["devices"] as const,
  lists: () => [...deviceKeys.all, "list"] as const,
  list: (filters?: DeviceListFilters) =>
    [...deviceKeys.lists(), filters ?? {}] as const,
  /** Keep literal ["house-kiosks", houseId] for compatibility with existing cache. */
  byHouse: (houseId: string) => ["house-kiosks", houseId] as const,
};

export const deviceService = {
  getAll: (filters?: DeviceListFilters): Promise<Device[]> =>
    fetchAllPages(async (page) => {
      const response = await apiClient.get<Device[]>("/devices", {
        params: { ...filters, ...page },
      });
      return response.data;
    }, DEVICES_PAGE_SIZE),

  create: async (data: DeviceCreate): Promise<DeviceCreateResponse> => {
    const response = await apiClient.post<DeviceCreateResponse>(
      "/devices",
      data,
    );
    return response.data;
  },

  update: async (deviceId: string, data: DeviceUpdate): Promise<Device> => {
    const response = await apiClient.put<Device>(`/devices/${deviceId}`, data);
    return response.data;
  },

  assignToHouse: async (deviceId: string, houseId: string): Promise<Device> => {
    const response = await apiClient.put<Device>(`/devices/${deviceId}/house`, {
      house_id: houseId,
    });
    return response.data;
  },

  unassignFromHouse: async (deviceId: string): Promise<Device> => {
    const response = await apiClient.delete<Device>(
      `/devices/${deviceId}/house`,
    );
    return response.data;
  },

  resetPassword: async (deviceId: string): Promise<{ password: string }> => {
    const response = await apiClient.post<{ password: string }>(
      `/devices/${deviceId}/reset-password`,
    );
    return response.data;
  },

  linkReader: async (
    deviceId: string,
    data: { reader_id: string },
    houseId?: string,
  ): Promise<Device> => {
    const response = await apiClient.post<Device>(
      `/devices/${deviceId}/reader/link`,
      data,
      { params: houseId ? { house_id: houseId } : undefined },
    );
    return response.data;
  },

  pairMockReader: async (
    deviceId: string,
    data: { reader_id: string },
    houseId?: string,
  ): Promise<Device> => {
    const response = await apiClient.post<Device>(
      `/devices/${deviceId}/reader/mock/pair`,
      data,
      { params: houseId ? { house_id: houseId } : undefined },
    );
    return response.data;
  },

  refreshSumupReader: async (
    deviceId: string,
    houseId?: string,
  ): Promise<PaymentReader> => {
    const response = await apiClient.post<PaymentReader>(
      `/devices/${deviceId}/reader/sumup/refresh`,
      undefined,
      { params: houseId ? { house_id: houseId } : undefined },
    );
    return response.data;
  },

  unlinkReader: async (deviceId: string, houseId?: string): Promise<Device> => {
    const response = await apiClient.delete<Device>(
      `/devices/${deviceId}/reader`,
      { params: houseId ? { house_id: houseId } : undefined },
    );
    return response.data;
  },
};
